import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import { BlogBreadcrumbs } from "../components/BlogBreadcrumbs";
import { useConfirm } from "../../../common/confirmDialog/useConfirm";
import { fetchTags, createTag, deleteTag } from "../tagSlice";
import { inputSx } from "../blogTheme";

export default function TagList() {
  const dispatch = useDispatch();
  const { tags, loading, error } = useSelector((state) => state.tag);
  const [newTag, setNewTag]         = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const confirm = useConfirm();

  useEffect(() => { dispatch(fetchTags()); }, [dispatch]);

  const handleAdd = () => {
    const name = newTag.trim();
    if (!name) return;
    dispatch(createTag({ name }));
    setNewTag("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleAdd();
  };

  const handleDelete = async (id) => {
    const ok = await confirm({ title: "Delete Tag", description: "Are you sure you want to delete this tag?", confirmText: "Delete", destructive: true });
    if (!ok) return;
    setDeletingId(id);
    await dispatch(deleteTag(id));
    setDeletingId(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <BlogBreadcrumbs items={[{ label: "Blog", href: "/blog" }, { label: "Tags" }]} />
      <Typography variant="h5" sx={{ fontWeight: 700, color: "#362B24", fontFamily: "Heebo", mb: 2 }}>Tags</Typography>

      <Box sx={{ display: "flex", gap: 1, mb: 3, alignItems: "center" }}>
        <TextField
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="New tag name"
          size="small"
          sx={{ width: 220, ...inputSx }}
        />
        <IconButton
          onClick={handleAdd}
          sx={{
            background: "linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)",
            color: "#fff",
            borderRadius: "12px",
            width: 36,
            height: 36,
            "&:hover": { opacity: 0.9 },
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>

      {loading && <CircularProgress size={24} sx={{ color: "#065F46" }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {tags.map((tag) => (
          <Chip
            key={tag._id}
            label={
              deletingId === tag._id
                ? <CircularProgress size={12} sx={{ color: "#065F46", verticalAlign: "middle" }} />
                : `${tag.name} (${tag.postCount ?? 0})`
            }
            onDelete={deletingId === tag._id ? undefined : () => handleDelete(tag._id)}
            variant="outlined"
            disabled={deletingId === tag._id}
            sx={{
              borderColor: "#065F46",
              color: "#362B24",
              fontFamily: "Heebo",
              opacity: deletingId === tag._id ? 0.6 : 1,
              "& .MuiChip-deleteIcon": { color: "#EA001B" },
            }}
          />
        ))}
        {!loading && tags.length === 0 && (
          <Typography sx={{ color: "#362B24", opacity: 0.5, fontFamily: "Heebo" }}>No tags yet.</Typography>
        )}
      </Box>

    </Box>
  );
}
